import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  DocumentMetadataUpdate,
  useKnowledgeDocumentDataContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { useGeckoPermissions } from 'features/permissions'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import {
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Pencil,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  RolodexEntryType,
} from 'types'

const Container = styled.div`
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-top: 0.0625rem solid ${({ theme }) => theme.forecast.border};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.pageBackground1};
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const Content = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'grid' : 'none')};
  grid-template-columns: 1fr 1fr;
  gap: 0.375rem;
  padding: 0.5rem;
  border-top: 0.0625rem solid ${({ theme }) => theme.forecast.border};
`

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.375rem 0.5rem;
  background: rgb(38, 38, 38);
  border-radius: 0.25rem;
`

const EditInput = styled.input`
  width: 100%;
  padding: 0.25rem 0.375rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) => theme.forecast.pageBackground1};
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

type MetadataField = {
  key: keyof DocumentMetadataUpdate
  label: string
}

const METADATA_FIELDS: MetadataField[] = [
  { key: 'system', label: 'System' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'document_type', label: 'Doc Type' },
  { key: 'drawing_number', label: 'Drawing #' },
  { key: 'revision_code', label: 'Revision' },
  { key: 'revision_date', label: 'Rev. Date' },
]

export function DocumentMetadataSection() {
  const { hasPermissions } = useGeckoPermissions()
  const { currentDocumentId } = useKnowledgeDocumentUXContext()
  const { siteID, updateDocumentMetadata } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const documentLoadOptions = useMemo(
    () =>
      isPipelineConfigScopeLoading || !currentDocumentId
        ? undefined
        : {
            type: [RolodexEntryType.SYSTEM_DOCUMENT],
            ids: [currentDocumentId],
            tags: getPipelineConfigScopedTags({ site: siteID }),
            limit: 1,
          },
    [
      currentDocumentId,
      getPipelineConfigScopedTags,
      isPipelineConfigScopeLoading,
      siteID,
    ]
  )
  const { data: systemDocumentEntries } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      documentLoadOptions
    )
  const selectedDocument = systemDocumentEntries?.[0]

  const [isExpanded, setIsExpanded] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editedValues, setEditedValues] = useState<DocumentMetadataUpdate>({})

  const canEditMetadata = hasPermissions([
    PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  ])

  useEffect(() => {
    if (selectedDocument) {
      setEditedValues({
        system: selectedDocument.entry.data.system || '',
        vendor_name: selectedDocument.entry.data.vendor_name || '',
        document_type: selectedDocument.entry.data.document_type || '',
        drawing_number: selectedDocument.entry.data.drawing_number || '',
        revision_code: selectedDocument.entry.data.revision_code || '',
        revision_date: selectedDocument.entry.data.revision_date || '',
      })
    }
  }, [selectedDocument])

  const handleToggleExpand = useCallback(() => {
    if (!isEditing) setIsExpanded((prev) => !prev)
  }, [isEditing])

  const handleEditClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
    setIsExpanded(true)
  }, [])

  const handleCancelEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(false)
      if (selectedDocument) {
        setEditedValues({
          system: selectedDocument.entry.data.system || '',
          vendor_name: selectedDocument.entry.data.vendor_name || '',
          document_type: selectedDocument.entry.data.document_type || '',
          drawing_number: selectedDocument.entry.data.drawing_number || '',
          revision_code: selectedDocument.entry.data.revision_code || '',
          revision_date: selectedDocument.entry.data.revision_date || '',
        })
      }
    },
    [selectedDocument]
  )

  const handleSaveEdit = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!selectedDocument) return

      setIsSaving(true)
      try {
        await updateDocumentMetadata(selectedDocument.entry.id, editedValues)
        setIsEditing(false)
      } catch {
        // Error handled in context
      } finally {
        setIsSaving(false)
      }
    },
    [selectedDocument, editedValues, updateDocumentMetadata]
  )

  const handleInputChange = useCallback(
    (key: keyof DocumentMetadataUpdate, value: string) => {
      setEditedValues((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  if (!currentDocumentId || !selectedDocument) return null

  return (
    <Container>
      <Header role='button' tabIndex={0} onClick={handleToggleExpand}>
        <Box display='inlineFlex' align='center' gap='xsPlus'>
          <FileText size={14} />
          <Text variant='caption' weight='semibold'>
            Document Metadata
          </Text>
        </Box>
        <HeaderActions>
          {canEditMetadata &&
            (isEditing ? (
              <>
                <Button
                  variant='ghost'
                  size='icon-sm'
                  disabled={isSaving}
                  title='Cancel'
                  onClick={handleCancelEdit}>
                  <X size={14} />
                </Button>
                <Button
                  variant='ghost'
                  size='icon-sm'
                  disabled={isSaving}
                  title='Save'
                  onClick={handleSaveEdit}>
                  <Check size={14} />
                </Button>
              </>
            ) : (
              <Button
                variant='ghost'
                size='icon-sm'
                title='Edit Metadata'
                onClick={handleEditClick}>
                <Pencil size={14} />
              </Button>
            ))}
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </HeaderActions>
      </Header>
      <Content $isExpanded={isExpanded}>
        {METADATA_FIELDS.map((field) => {
          const rawValue =
            selectedDocument.entry.data[
              field.key as keyof typeof selectedDocument.entry.data
            ]
          const displayValue = typeof rawValue === 'string' ? rawValue : ''

          return (
            <MetadataItem key={field.key}>
              <Text as='div' variant='overline' color='muted'>
                {field.label}
              </Text>
              {isEditing ? (
                <EditInput
                  type='text'
                  value={editedValues[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  disabled={isSaving}
                />
              ) : (
                <Text as='div' variant='label'>
                  {displayValue || (
                    <Text as='span' color='muted' italic>
                      Not set
                    </Text>
                  )}
                </Text>
              )}
            </MetadataItem>
          )
        })}
      </Content>
    </Container>
  )
}
